/* eslint-disable @typescript-eslint/no-explicit-any -- legacy external-data boundary types; migrate incrementally with runtime validation */
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import type { User } from '@supabase/supabase-js'
import { supabase, getCurrentRole, type UserRole } from '../lib/supabase'

interface AuthContextType {
  user: User | null
  role: UserRole
  loading: boolean
  signIn: (email: string, password: string) => Promise<{ error: any }>
  signOut: () => Promise<void>
  resetPassword: (email: string) => Promise<{ error: any }>
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextType | undefined>(undefined)

// eslint-disable-next-line react-refresh/only-export-components
export function useOptionalAuth() { return useContext(AuthContext) }

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [role, setRole] = useState<UserRole>(null)
  const [loading, setLoading] = useState(true)
  const roleLoadGeneration = useRef(0)

  useEffect(() => {
    let isMounted = true
    let activeUserId: string | null = null
    let hasResolvedRole = false
    let receivedAuthEvent = false
    const loadRole = async (sessionUser: User | null) => {
      const generation = ++roleLoadGeneration.current
      // Resolve a new identity before mounting protected content. Rechecking the
      // same identity in the background preserves open drafts and PDF previews;
      // a changed, missing or failed live role still removes access on completion.
      if (activeUserId !== (sessionUser?.id ?? null) || !hasResolvedRole) {
        activeUserId = sessionUser?.id ?? null
        hasResolvedRole = false
        setLoading(true)
        setRole(null)
      }
      if (!sessionUser) {
        setRole(null)
        hasResolvedRole = true
        if (isMounted) setLoading(false)
        return
      }
      try {
        const r = await getCurrentRole()
        if (isMounted && generation === roleLoadGeneration.current) setRole(r)
      } catch (err) {
        console.error('Failed to load role:', err)
        if (isMounted && generation === roleLoadGeneration.current) setRole(null)
      } finally {
        if (isMounted && generation === roleLoadGeneration.current) {
          hasResolvedRole = true
          setLoading(false)
        }
      }
    }

    // Keep loading true until we get the initial auth event
    setLoading(true)

    // Listen for auth changes - this handles session updates and refreshes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!isMounted) return
      receivedAuthEvent = true
      setUser(session?.user ?? null)
      void loadRole(session?.user ?? null)
    })

    // Kick off initial session fetch (onAuthStateChange will also fire INITIAL_SESSION)
    supabase.auth.getSession().then(({ data }) => {
      if (!isMounted || receivedAuthEvent) return
      setUser(data.session?.user ?? null)
      void loadRole(data.session?.user ?? null)
    }).catch(err => {
      if (!isMounted || receivedAuthEvent) return
      roleLoadGeneration.current += 1
      console.error('getSession error:', err)
      if (isMounted) {
        setUser(null)
        setRole(null)
        setLoading(false)
      }
    })

    return () => {
      isMounted = false
      roleLoadGeneration.current += 1
      subscription.unsubscribe()
    }
  }, [])

  const signIn = async (email: string, password: string) => {
    setLoading(true)
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    // Session + role will be handled by onAuthStateChange; no redirect logic here
    return { error }
  }

  const signOut = async () => {
    setLoading(true)
    await supabase.auth.signOut()
    // onAuthStateChange will clear state and loading
  }

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    })
    return { error }
  }

  const value = {
    user,
    role,
    loading,
    signIn,
    signOut,
    resetPassword,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// The provider and its companion hook intentionally share one module.
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
